import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit2, Trash2, X, ShieldCheck, User as UserIcon, PenSquare } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const ROLES = [
  { value: "super_admin", label: "Super Admin", desc: "Akses penuh, termasuk user management", icon: ShieldCheck, color: "text-red-600 bg-red-50" },
  { value: "admin", label: "Admin", desc: "Kelola konten & pengaturan situs", icon: UserIcon, color: "text-blue-600 bg-blue-50" },
  { value: "editor", label: "Editor", desc: "Hanya menambah/mengedit konten", icon: PenSquare, color: "text-emerald-600 bg-emerald-50" },
];

function RoleBadge({ role }) {
  const r = ROLES.find((x) => x.value === role) || ROLES[2];
  const Icon = r.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${r.color}`}>
      <Icon className="h-3 w-3" /> {r.label}
    </span>
  );
}

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "editor" });
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/users")
      .then((r) => setUsers(r.data.items || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm({ email: "", password: "", name: "", role: "editor" });
    setEditing({});
  };

  const openEdit = (u) => {
    setForm({ email: u.email, password: "", name: u.name || "", role: u.role });
    setEditing(u);
  };

  const close = () => { setEditing(null); setForm({ email: "", password: "", name: "", role: "editor" }); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing && editing.id) {
        const payload = { name: form.name, email: form.email, role: form.role };
        if (form.password) payload.password = form.password;
        await api.patch(`/admin/users/${editing.id}`, payload);
        toast.success("User diperbarui");
      } else {
        if (!form.password || form.password.length < 8) {
          toast.error("Password minimal 8 karakter");
          setSaving(false);
          return;
        }
        await api.post("/admin/users", form);
        toast.success("User berhasil dibuat");
      }
      close();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (u) => {
    if (!confirm(`Hapus user "${u.email}"?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User dihapus");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Manajemen User</h1>
          <p className="text-slate-600 mt-1">Kelola akun admin, editor, dan super admin.</p>
        </div>
        <button onClick={openNew} className="btn-primary" data-testid="admin-users-add">
          <Plus className="h-4 w-4" /> Tambah User
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        {ROLES.map((r) => (
          <div key={r.value} className="card-sf p-5">
            <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${r.color}`}>
              <r.icon className="h-3 w-3" /> {r.label}
            </div>
            <p className="mt-2 text-xs text-slate-600 leading-relaxed">{r.desc}</p>
          </div>
        ))}
      </div>

      <div className="card-sf overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Nama</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Email</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Role</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Dibuat</th>
              <th className="text-right px-5 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="p-8 text-center text-slate-500">Memuat...</td></tr>}
            {!loading && users.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-slate-500">Belum ada user.</td></tr>}
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-5 py-3 text-sm font-semibold text-slate-800">
                  {u.name}
                  {u.id === currentUser?.id && <span className="ml-2 text-[10px] uppercase tracking-wider text-[var(--brand-orange)] font-bold">(Anda)</span>}
                </td>
                <td className="px-5 py-3 text-sm text-slate-700">{u.email}</td>
                <td className="px-5 py-3"><RoleBadge role={u.role} /></td>
                <td className="px-5 py-3 text-xs text-slate-500">{(u.created_at || "").slice(0, 10)}</td>
                <td className="px-5 py-3 text-right">
                  <div className="inline-flex gap-2">
                    <button onClick={() => openEdit(u)} className="h-8 w-8 rounded-lg border border-slate-200 hover:border-[var(--brand-blue)] hover:text-[var(--brand-blue)] flex items-center justify-center" data-testid={`user-edit-${u.id}`}>
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => remove(u)}
                      disabled={u.id === currentUser?.id}
                      className="h-8 w-8 rounded-lg border border-slate-200 hover:border-red-500 hover:text-red-500 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
                      data-testid={`user-delete-${u.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing !== null && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-start justify-center p-4 overflow-y-auto py-10" onClick={close}>
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="user-form-modal">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900">{editing.id ? "Edit User" : "Tambah User"}</h2>
              <button onClick={close} className="h-9 w-9 rounded-full hover:bg-slate-100 flex items-center justify-center"><X className="h-4 w-4" /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Nama</label>
                <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[var(--brand-blue)]" data-testid="user-form-name" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Email <span className="text-[var(--brand-orange)]">*</span></label>
                <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[var(--brand-blue)]" data-testid="user-form-email" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">
                  Password {editing.id ? "(kosongkan jika tidak diubah)" : <span className="text-[var(--brand-orange)]">*</span>}
                </label>
                <input type="password" value={form.password} minLength={editing.id ? 0 : 8} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing.id ? "Biarkan kosong = tidak berubah" : "Minimal 8 karakter"} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[var(--brand-blue)]" data-testid="user-form-password" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1.5">Role <span className="text-[var(--brand-orange)]">*</span></label>
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-[var(--brand-blue)]" data-testid="user-form-role">
                  {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label} — {r.desc}</option>)}
                </select>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                <button type="button" onClick={close} className="btn-ghost">Batal</button>
                <button type="submit" disabled={saving} className="btn-secondary" data-testid="user-form-save">
                  {saving ? "Menyimpan..." : "Simpan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
